"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonButtons,
  IonBackButton,
  IonToast,
  IonSegment,
  IonSegmentButton,
  IonLabel,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  TablePagination,
  InputAdornment,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import {
  Check,
  ArrowBack,
  Search,
  FilterList,
  Visibility,
  Refresh,
  CheckCircle,
  Pending,
  Save,
  Close,
  FactCheck,
  Assignment,
} from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { 
  getAuditPurchaseOrderRequests,
  getMyPurchaseOrderRequests,
  updateRequestStatus
} from "../../api/accounting/accounting"

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
  },
}))

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}))

// Define types
interface RequestItem {
  id: number
  item_id: string
  stock_name: string
  requested_qty: string
  vendor_id: string
  status: string
  audited_qty?: string
  cmd_approved_qty?: string
  supplied_qty?: string
  po_approved_qty?: string | null
  purchase_requisition_id?: string
  notes?: string
  po_received_qty?: string
  // For editing
  audit_qty_input?: string
}

interface PurchaseOrder {
  id: number
  po_number: string
  notes: string | null
  status: string
  created_by: string
  requested_by_name: string
  created_at: string
  approved_at: string | null
  approved_by: string | null
  cmd_approved_at: string | null
  cmd_approved_by: string | null
  cmd_approval_status: string
  audit_approval_status: string
  branch_id: string
  business_id: string
  items: RequestItem[]
}

const AuditOfficerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'audited'>('pending')
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([])
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    start_date: null as Date | null,
    end_date: null as Date | null,
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    fetchPurchaseOrders()
  }, [activeTab, page, rowsPerPage])

  useEffect(() => {
    applyFilters()
  }, [purchaseOrders, filters])

  const fetchPurchaseOrders = async () => {
    setLoading(true)
    try {
      const params = {
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
        ...(filters.search && { search: filters.search }),
        ...(activeTab === 'pending' && { audit_status: 'pending' }),
        ...(activeTab === 'audited' && { audit_status: 'approved' }),
      }

      const response = await getAuditPurchaseOrderRequests(params)

      console.log('Purchase Orders Response:', response)

      if (response && response.data) {
        const ordersData = response.data.data || response.data
        
        const orders = ordersData.map((order: any) => ({
          id: order.id,
          po_number: order.po_number || order.purchase_requisition_id,
          notes: order.notes,
          status: order.status,
          created_by: order.created_by,
          requested_by_name: order.requested_by_name,
          created_at: order.created_at,
          approved_at: order.approved_at,
          approved_by: order.approved_by,
          cmd_approved_at: order.cmd_approved_at,
          cmd_approved_by: order.cmd_approved_by,
          cmd_approval_status: order.cmd_approval_status || 'pending',
          audit_approval_status: order.audit_approval_status || 'pending',
          branch_id: order.branch_id,
          business_id: order.business_id,
          items: (order.items || []).map((item: any) => ({
            id: item.id,
            item_id: item.item_id,
            stock_name: item.stock_name,
            requested_qty: item.requested_qty,
            vendor_id: item.vendor_id,
            status: item.status,
            audited_qty: item.audited_qty || '0.00',
            cmd_approved_qty: item.cmd_approved_qty || '0.00',
            supplied_qty: item.supplied_qty || '0.00',
            po_approved_qty: item.po_approved_qty || item.requested_qty,
            po_received_qty: item.po_received_qty || '0.00',
            purchase_requisition_id: item.purchase_requisition_id,
            notes: item.notes,
            audit_qty_input: item.po_received_qty || item.supplied_qty || item.audited_qty || '',
          }))
        }))

        setPurchaseOrders(orders)
        
        // Check if response has pagination info
        if (response.data.meta && response.data.meta.total) {
          setTotalCount(response.data.meta.total)
        } else if (response.data.total) {
          setTotalCount(response.data.total)
        } else {
          setTotalCount(orders.length)
        }
      }
    } catch (error) {
      console.error("Failed to fetch purchase orders", error)
      showErrorToast('Failed to fetch purchase orders')
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...purchaseOrders]

    if (filters.search) {
      const query = filters.search.toLowerCase()
      filtered = filtered.filter(order =>
        order.po_number.toLowerCase().includes(query) ||
        order.requested_by_name.toLowerCase().includes(query) ||
        order.items.some(item => item.stock_name.toLowerCase().includes(query))
      )
    }

    setFilteredOrders(filtered)
  }

  const showSuccessToast = (message: string) => {
    setToastMessage(message)
    setToastType('success')
    setShowToast(true)
  }

  const showErrorToast = (message: string) => {
    setToastMessage(message)
    setToastType('error')
    setShowToast(true)
  }

  const handleViewOrder = (order: PurchaseOrder) => {
    setSelectedOrder(order)
    setShowDetailDialog(true)
  }

  const handleUpdateItemQuantity = (orderId: number, itemId: number, value: string) => {
    setPurchaseOrders(prev => prev.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          items: order.items.map(item => {
            if (item.id === itemId) {
              return { ...item, audit_qty_input: value }
            }
            return item
          })
        }
      }
      return order
    }))

    if (selectedOrder && selectedOrder.id === orderId) {
      setSelectedOrder(prev => {
        if (!prev) return null
        return {
          ...prev,
          items: prev.items.map(item => {
            if (item.id === itemId) {
              return { ...item, audit_qty_input: value }
            }
            return item
          })
        }
      })
    }
  }

  const handleSubmitAudit = () => {
    if (!selectedOrder) return
    
    // Validate all items have audit quantities
    const hasEmptyAudit = selectedOrder.items.some(item => {
      const auditQty = item.audit_qty_input?.trim()
      return !auditQty || auditQty === '' || isNaN(parseFloat(auditQty))
    })
    
    if (hasEmptyAudit) {
      showErrorToast('Please enter valid audit quantity for all items')
      return
    }

    setShowConfirmDialog(true)
  }

  const handleConfirmSubmit = async () => {
    if (!selectedOrder) return

    setLoading(true)
    try {
      const payload = {
        purchase_requisition_id: selectedOrder.po_number,
        action: 'audit_items',
        items: selectedOrder.items.map(item => ({
          item_id: item.item_id,
          audited_qty: item.audit_qty_input,
          notes: item.notes || ''
        }))
      }

      console.log('Submitting audit payload:', payload)
      const response = await updateRequestStatus(payload)
      console.log('Audit response:', response)

      if (response && (response.status === 200 || response.status === 201 || response.data?.status === 'success' || response.status === true)) {
        showSuccessToast('Audit submitted successfully')
        setShowDetailDialog(false)
        setShowConfirmDialog(false)
        fetchPurchaseOrders() 
      } else {
        showErrorToast('Failed to submit audit')
      }
    } catch (error) {
      console.error('Error submitting audit:', error)
      showErrorToast('Failed to submit audit: ' + (error as any).message)
    } finally {
      setLoading(false)
    }
  }

  const getStatusChip = (status: string) => {
    const statusMap: any = {
      pending: { label: 'Pending Audit', color: 'warning', icon: <Pending /> },
      audited: { label: 'Audited', color: 'success', icon: <CheckCircle /> },
      completed: { label: 'Completed', color: 'success', icon: <CheckCircle /> },
      approved: { label: 'Approved', color: 'info', icon: <CheckCircle /> },
    }

    const statusInfo = statusMap[status] || { label: status, color: 'default', icon: null }

    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color as any}
        size="small"
        icon={statusInfo.icon}
        variant="outlined"
      />
    )
  }

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(0)
  }

  const renderPendingTab = () => (
    <>
   

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "#fff3e0", borderLeft: "4px solid #ff9800" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#ff9800" }}>
                {purchaseOrders.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Audits
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "#e8f5e9", borderLeft: "4px solid #4caf50" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#4caf50" }}>
                {purchaseOrders.reduce((sum, order) => sum + order.items.length, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: "#f3e5f5", borderLeft: "4px solid #9c27b0" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#9c27b0" }}>
                {purchaseOrders.reduce((sum, order) => {
                  const totalReceived = order.items.reduce((s, item) => s + parseFloat(item.po_received_qty || '0'), 0)
                  return sum + totalReceived
                }, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Units Received
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList /> Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Search PO Number or Item"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Start Date"
                value={filters.start_date}
                onChange={(newValue) => handleFilterChange('start_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="End Date"
                value={filters.end_date}
                onChange={(newValue) => handleFilterChange('end_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({
                  search: '',
                  start_date: null,
                  end_date: null,
                })}
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Orders Pending Audit ({filteredOrders.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchPurchaseOrders}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filteredOrders.length === 0 ? (
            <Alert severity="info" sx={{ textAlign: 'center', py: 3 }}>
              No orders pending audit
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>PO Number</strong></TableCell>
                      <TableCell><strong>Requested By</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Items</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {order.po_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{order.requested_by_name}</Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(order.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Typography>{order.items.length} items</Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(order.status)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Audit Order">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewOrder(order)}
                              >
                                <Assignment />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
              />
            </>
          )}
        </CardContent>
      </Card>
    </>
  )

  const renderAuditedTab = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Audited Orders ({filteredOrders.length})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPurchaseOrders}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Alert severity="info" sx={{ textAlign: 'center', py: 3 }}>
            No audited orders found
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>PO Number</strong></TableCell>
                    <TableCell><strong>Requested By</strong></TableCell>
                    <TableCell><strong>Dat Audit</strong></TableCell>
                    <TableCell><strong>Items</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.po_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{order.requested_by_name}</Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Typography>{order.items.length} items</Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip('audited')}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  )

  return (
    <IonPage className="pageDown">
      <IonHeader>
         <Box
        sx={{
          bgcolor: "#6a1b9a",
          color: "white",
          py: 3,
          px: 4,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <IconButton sx={{ color: "white" }} onClick={() => window.history.back()}>
            <ArrowBack />
          </IconButton>
          <FactCheck sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Audit Officer Dashboard
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9, ml: 7 }}>
          Review and audit received purchase orders
        </Typography>
      </Box>
        <IonToolbar>
          <IonSegment 
            value={activeTab} 
            onIonChange={(e) => setActiveTab(e.detail.value as any)}
          >
            <IonSegmentButton value="pending">
              <IonLabel>Pending Audit</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="audited">
              <IonLabel>Audited</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <PageContainer>
          {activeTab === 'pending' ? renderPendingTab() : renderAuditedTab()}
        </PageContainer>

        {/* Audit Dialog */}
        <Dialog 
          open={showDetailDialog} 
          onClose={() => setShowDetailDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Audit Purchase Order: {selectedOrder?.po_number}
              </Typography>
              <IconButton onClick={() => setShowDetailDialog(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Requested By</Typography>
                    <Typography variant="body1">{selectedOrder.requested_by_name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Created Date</Typography>
                    <Typography variant="body1">{new Date(selectedOrder.created_at).toLocaleString()}</Typography>
                  </Grid>
                  {selectedOrder.notes && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Notes</Typography>
                      <Typography variant="body1">{selectedOrder.notes}</Typography>
                    </Grid>
                  )}
                </Grid>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Please verify the physical quantity received for each item and enter the audited quantity below.
                </Alert>

                <Typography variant="h6" sx={{ mb: 2 }}>Items to Audit</Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Requested</TableCell>
                        <TableCell align="right">PO Approved</TableCell>
                        <TableCell align="right">CMD Approved</TableCell>
                        <TableCell align="right">Supplied/Received</TableCell>
                        <TableCell align="right">Audited Quantity</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Typography variant="body2">{item.stock_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {item.item_id}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{item.requested_qty}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={item.po_approved_qty || '0'} 
                              size="small" 
                              color="secondary"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={item.cmd_approved_qty || '0'} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={item.po_received_qty || item.supplied_qty || '0'} 
                              size="small" 
                              color="info"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={item.audit_qty_input}
                              onChange={(e) => handleUpdateItemQuantity(selectedOrder.id, item.id, e.target.value)}
                              placeholder="Enter audited qty"
                              sx={{ width: 150 }}
                              required
                              disabled={selectedOrder.audit_approval_status === 'approved'}
                            />
                          </TableCell>
                          <TableCell>
                            {item.audited_qty && parseFloat(item.audited_qty) > 0 ? (
                              <Chip label="Audited" size="small" color="success" />
                            ) : (
                              <Chip label="Pending" size="small" color="warning" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailDialog(false)}>Cancel</Button>
            {selectedOrder?.audit_approval_status !== 'approved' && (
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<Check />}
                onClick={handleSubmitAudit}
              >
                Submit Audit
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
        >
          <DialogTitle>Confirm Audit Submission</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to submit the audit for this Purchase Order?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              PO Number: <strong>{selectedOrder?.po_number}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmDialog(false)} color="inherit">
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleConfirmSubmit}
              startIcon={<Check />}
            >
              Confirm & Submit
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message={'Loading...'} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastType}
        />
      </IonContent>
    </IonPage>
  )
}

export default AuditOfficerPage